import { useCallback, useEffect } from 'react';

import store, { useRecoilState, useSetRecoilState } from '@store';
import { useDebounce, useLocalStorage } from '@hooks';

import { useSearch } from './useSearch';

export const useSearchInput = () => {
  const [, { getSearchResults, getSearchSuggestions, setSearchData }] =
    useSearch();
  const [_, setSearchQuery] = useLocalStorage('searchTerm', '');

  const [rawTerm, setRawTerm] = useRecoilState(store.rawTerm);
  const [term, setTerm] = useRecoilState(store.term);
  const setSearchIsFocused = useSetRecoilState(store.searchIsFocused);
  const setLoading = useSetRecoilState(store.searchLoading);
  const setRecoilRawTerm = useSetRecoilState(store.rawTerm);
  const debouncedTerm = useDebounce(rawTerm, 300);

  const isSearching = rawTerm && rawTerm !== '';

  // clear input
  const resetRawTerm = useCallback(() => {
    setRawTerm('');
    setSearchQuery('');
  }, []);

  const setRawTermAndSearchQuery = useCallback((_rawTerm) => {
    setRawTerm(_rawTerm);
    setRecoilRawTerm(_rawTerm);
    if (_rawTerm && _rawTerm !== '') {
      setSearchQuery(_rawTerm);
    } else {
      setSearchQuery('');
    }
  }, []);

  // check for search suggestions, then return results
  const getSuggestionsThenResults = useCallback(async () => {
    try {
      if (!debouncedTerm) {
        setSearchData(null);
      }

      setLoading(true);

      const data = await getSearchSuggestions({ query: debouncedTerm });

      const exactTerm = data?.['corrected-query'] || data?.query;
      const isAutocomplete = data?.suggested?.type === 'completed';
      const searchTerm = isAutocomplete ? data?.suggested?.text : exactTerm;
      await getSearchResults({ query: searchTerm, isAutocomplete });

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error.message);
    }
  }, [debouncedTerm]);

  useEffect(() => {
    getSuggestionsThenResults();
  }, [debouncedTerm]);

  useEffect(() => {
    if (term !== debouncedTerm) {
      setTerm(debouncedTerm);
    }
  }, [debouncedTerm]);

  return [
    { term, rawTerm, isSearching },
    {
      setTerm,
      setRawTerm: setRawTermAndSearchQuery,
      resetRawTerm,
      setSearchIsFocused,
    },
  ];
};
